import { Box, Divider, Typography } from "@mui/material";

interface Props {
  label: string;
  /** Omite el divider superior — usar en la primera sección de la card. */
  first?: boolean;
  /** Rango o nota alineada a la derecha del label (`.cotiz-card-meta`). */
  meta?: string;
  /**
   * `cotizador`: `.cotiz-card-h` de los cotizadores v2 (antes `SectionHeaderCotizador`). Label en
   * `text.sectionLabel` de la marca y divider sin atenuar.
   * `underline`: `.section-h` del diseño, fuera de una card: título de 15px con la línea debajo (ej. "Tu unidad"
   * en el cotizador de WuWei). `first` no aplica.
   */
  variant?: "default" | "cotizador" | "underline";
}

const Meta = ({ children }: { children: string }) => (
  <Typography variant="body3" color="tokens.textTertiary" sx={{ fontVariantNumeric: "tabular-nums" }}>
    {children}
  </Typography>
);

/**
 * Replica `.sub-h` + `.sub-divider` de styles.css (sección "premium fintech"):
 * encabezado de sub-sección dentro de una card, separado por un divider sutil.
 */
export const SectionHeader = ({ label, first, meta, variant = "default" }: Props) => {
  if (variant === "underline") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: "4px 12px",
          mt: "28px",
          mb: "12px",
          pb: "10px",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark" }}>
          {label}
        </Typography>
        {meta && <Meta>{meta}</Meta>}
      </Box>
    );
  }

  if (variant === "cotizador") {
    return (
      <>
        {!first && <Divider sx={{ my: "22px", borderColor: "divider" }} />}
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: "14px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              letterSpacing: "-0.01em",
              // `text.sectionLabel` de la marca; si no lo define, el azul marino (`--ld-navy`).
              color: (theme) => theme.palette.components?.text?.sectionLabel ?? theme.palette.primary.dark,
            }}
          >
            {label}
          </Typography>
          {meta && <Meta>{meta}</Meta>}
        </Box>
      </>
    );
  }

  const labelText = (
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: "primary.dark",
        mb: meta ? 0 : "12px",
      }}
    >
      {label}
    </Typography>
  );

  return (
    <>
      {/* `opacity` a propósito: dentro de una card la línea va más tenue que un separador normal. */}
      {!first && <Divider sx={{ mt: "20px", mb: "18px", opacity: 0.6 }} />}
      {meta ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: "12px",
          }}
        >
          {labelText}
          <Meta>{meta}</Meta>
        </Box>
      ) : (
        labelText
      )}
    </>
  );
};
